import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { Form, Formik } from "formik";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { AppDispatch } from "../../redux/store/store";
import { IConfirmPasswordResetPayload } from "../../utility/types/userType";
import { confirmPassWordReset } from "../../redux/actions/userActions";
import { confirmPasswordResetValidation } from "../../utility/validation/passwordResetValidation";
import { colors } from "../../utility/types/constant";
import LockResetIcon from "@mui/icons-material/LockReset";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface PasswordResetVerifyProps {
  open: boolean;
  handleClose: () => void;
}

const PasswordResetVerify = (props: PasswordResetVerifyProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { open, handleClose } = props;

  const [successAlert, setSuccessAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const initialValues: IConfirmPasswordResetPayload = {
    email: "",
    code: "",
    newPassword: "",
  };

  const handleCloseSuccessAlert = () => setSuccessAlert(false);
  const handleCloseErrorAlert = () => setErrorAlert("");

  const handleSubmit = async (userData: IConfirmPasswordResetPayload) => {
    setIsLoading(true);
    try {
      await dispatch(confirmPassWordReset(userData)).unwrap();
      setSuccessAlert(true);
      setTimeout(() => {
        navigate("/circle/sign-in");
      }, 3000);
    } catch (err: any) {
      setSuccessAlert(false);
      const message =
        (typeof err === "string" && err) ||
        err?.message ||
        err?.error ||
        "Invalid credentials. Please try again.";
      setErrorAlert(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
        },
      }}
    >
      {/* Header band */}
      <Box
        sx={{
          bgcolor: colors.primary,
          px: 4,
          pt: 4,
          pb: 3,
          position: "relative",
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: alpha("#fff", 0.5),
            "&:hover": { color: "#fff", bgcolor: alpha("#fff", 0.08) },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Icon + title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: alpha(colors.mainSecondary, 0.18),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LockResetIcon sx={{ color: colors.mainSecondary, fontSize: "1.5rem" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 800,
                color: "#fff",
                fontSize: "1.15rem",
                lineHeight: 1.2,
              }}
            >
              Verify & Reset Password
            </Typography>
            <Typography variant="caption" sx={{ color: alpha("#fff", 0.5) }}>
              Enter the code sent to your email along with your new password
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: { xs: 3, sm: 4 }, py: 3.5 }}>
        {successAlert && (
          <Alert
            severity="success"
            onClose={handleCloseSuccessAlert}
            icon={<CheckCircleIcon fontSize="inherit" />}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            Password reset successful — redirecting to sign in...
          </Alert>
        )}

        {errorAlert && (
          <Alert
            severity="error"
            onClose={handleCloseErrorAlert}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {errorAlert}
          </Alert>
        )}

        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={confirmPasswordResetValidation}
        >
          {({ values, handleChange, errors, touched }) => (
            <Form>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Verification Code"
                      name="code"
                      autoComplete="off"
                      onChange={handleChange}
                      value={values.code}
                      error={!!(errors?.code && touched?.code)}
                      helperText={errors.code && touched.code && errors.code}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Email Address"
                      name="email"
                      autoComplete="off"
                      onChange={handleChange}
                      value={values.email}
                      error={!!(errors?.email && touched?.email)}
                      helperText={errors.email && touched.email && errors.email}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      size="small"
                      fullWidth
                      label="New Password"
                      name="newPassword"
                      type="password"
                      autoComplete="new-password"
                      onChange={handleChange}
                      value={values.newPassword}
                      error={!!(errors?.newPassword && touched?.newPassword)}
                      helperText={
                        errors.newPassword &&
                        touched.newPassword &&
                        errors.newPassword
                      }
                    />
                  </Grid>
                </Grid>

                <Button
                  disabled={isLoading}
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 0.5,
                    bgcolor: colors.mainSecondary,
                    color: colors.primary,
                    fontWeight: 800,
                    py: 1.2,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    letterSpacing: 0.3,
                    boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
                    "&:hover": {
                      bgcolor: colors.mainSecondary,
                      boxShadow: `0 6px 20px ${alpha(colors.mainSecondary, 0.5)}`,
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={22} sx={{ color: colors.primary }} />
                  ) : (
                    "Reset Password"
                  )}
                </Button>

                <Typography
                  variant="caption"
                  sx={{ textAlign: "center", color: "text.disabled" }}
                >
                  © {new Date().getFullYear()} FundsyFi
                </Typography>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

PasswordResetVerify.propType = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

export default PasswordResetVerify;
